import { useCallback, useEffect, useRef } from 'preact/hooks';
import { logger } from '@accedo/logger';
import { stylesheet } from 'astroturf';
import { isDeviceWorkstation } from '##/utils/device';
import thumbnailVideo from '##/assets/videos/10-splash-file.mp4';
import SplashBackground1080p from '##/assets/images/10-Splash-StaticOnly.jpg';
import type { FunctionalComponent } from 'preact';
import type { Signal } from '@preact/signals';

const styles = stylesheet`
  @import '##/styles/import.scss';

  .loading-overlay {
    width: 100vw;
    height: 100vh;
  }

  .background {
    position: relative;
    height: 100vh;
    width: 100vw;
  }

  .splash-container {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 9999;
    width: 100vw;
    height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .splash-video-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background-color: transparent;
  }
`;

type SplashPlayerProps = {
  isSplashAnimationComplete: Signal<boolean>;
  loaded: boolean;
};

// Use this to display image as fallback on low-performing devices and workstations
const isLowPerforming = (): boolean => isDeviceWorkstation();
const SPLASH_TIMEOUT = 15000;
const SplashScreen: FunctionalComponent<SplashPlayerProps> = ({
  isSplashAnimationComplete,
  loaded,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const isVideoEnded = useRef(false);
  const fallbackTimeoutRef = useRef<number | null>(null);

  const onCanPlay = useCallback(() => {
    const video = videoRef.current;
    if (video && !video.played.length) {
      logger.info('SplashScreen', 'Video can play through, starting playback');
      video
        .play()
        .catch((error) => {
          logger.error('SplashScreen', 'Video playback failed:', error);
        });
    }
  }, []);

  const onVideoEnded = useCallback(() => {
    logger.info('SplashScreen', 'Video ended');
    // eslint-disable-next-line no-param-reassign
    isSplashAnimationComplete.value = true;
    isVideoEnded.current = true;
  }, [isSplashAnimationComplete]);

  const clearFallbackTimeout = useCallback(() => {
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
    }
  }, []);
  const fallbackTimeout = useCallback(() => {
    clearFallbackTimeout();
    fallbackTimeoutRef.current = window.setTimeout(() => {
      logger.info('SplashScreen', 'Fallback timeout reached');
      if (!isSplashAnimationComplete.peek()) {
        isVideoEnded.current = true;
        // eslint-disable-next-line no-param-reassign
        isSplashAnimationComplete.value = true;
      }
    }, SPLASH_TIMEOUT);
  }, [clearFallbackTimeout, isSplashAnimationComplete]);
  useEffect(() => {
    fallbackTimeout();
    return clearFallbackTimeout;
  }, [fallbackTimeout, clearFallbackTimeout]);

  useEffect(() => {
    if (loaded && (isLowPerforming() || isVideoEnded.current)) {
      // eslint-disable-next-line no-param-reassign
      isSplashAnimationComplete.value = true;
    }
  }, [loaded, isVideoEnded]);

  return (
    <>
      {isLowPerforming() ? (
        <div className={styles.loadingOverlay}>
          <img
            src={SplashBackground1080p}
            className={styles.background}
            alt="Splash Background"
          />
        </div>
      ) : (
        <div id="splash-screen" className={styles.splashContainer}>
          <video
            ref={videoRef}
            id="splash-video"
            className={styles.splashVideoContainer}
            preload="auto"
            onCanPlayThrough={onCanPlay}
            playsInline
            muted
            onEnded={onVideoEnded}
          >
            <source src={thumbnailVideo} type="video/mp4" />
          </video>
        </div>
      )}
    </>
  );
};

export default SplashScreen;
